import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, User, Camera, Lock, Save, Eye, EyeOff,
  CheckCircle, AlertCircle, Trash2, Upload
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AccountSettings() {
  const navigate = useNavigate();
  const { t, isRTL, dir } = useLanguage();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  
  // بيانات الملف الشخصي
  const [profileData, setProfileData] = useState({
    full_name: '',
    phone: '',
    profile_image: null
  });
  
  // بيانات كلمة المرور
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      setProfileData({
        full_name: response.data.full_name || '',
        phone: response.data.phone || '',
        profile_image: response.data.profile_image || null
      });
    } catch (err) {
      console.error('Error fetching user:', err);
      setError(t('msg.operationFailed') || 'فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/auth/profile`, {
        full_name: profileData.full_name,
        phone: profileData.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(t('msg.savedSuccessfully') || 'تم حفظ التغييرات بنجاح');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || t('msg.operationFailed') || 'فشل في حفظ التغييرات');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // التحقق من نوع الملف
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('settings.invalidImageType') || 'نوع الملف غير مدعوم. يرجى رفع صورة (JPG, PNG, GIF, WEBP)');
      return;
    }
    
    // التحقق من الحجم (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(t('settings.imageTooLarge') || 'حجم الصورة كبير جداً. الحد الأقصى 5MB');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/auth/upload-profile-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setProfileData(prev => ({ ...prev, profile_image: response.data.profile_image }));
      setSuccess(t('settings.imageUploaded') || 'تم رفع الصورة بنجاح');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || t('msg.operationFailed') || 'فشل في رفع الصورة');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!window.confirm(t('settings.confirmDeleteImage') || 'هل تريد حذف صورة البروفايل؟')) return;
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API}/auth/profile-image`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfileData(prev => ({ ...prev, profile_image: null }));
      setSuccess(t('settings.imageDeleted') || 'تم حذف الصورة بنجاح');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || t('msg.operationFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // التحقق من تطابق كلمة المرور
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError(t('settings.passwordMismatch') || 'كلمة المرور الجديدة غير متطابقة');
      return;
    }
    
    // التحقق من طول كلمة المرور
    if (passwordData.new_password.length < 6) {
      setError(t('settings.passwordTooShort') || 'كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/auth/change-password`, {
        old_password: passwordData.old_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess(t('settings.passwordChanged') || 'تم تغيير كلمة المرور بنجاح');
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || t('msg.operationFailed') || 'فشل في تغيير كلمة المرور');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" dir={dir}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('msg.loading') || 'جاري التحميل...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" dir={dir}>
      {/* Header */}
      <header className="bg-primary text-white py-4 px-6 shadow-lg">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              data-testid="back-btn"
            >
              <ArrowRight className="w-5 h-5" />
              {t('btn.back') || 'رجوع'}
            </button>
            <h1 className="text-xl font-heading font-bold">{t('settings.accountSettings') || 'إعدادات الحساب'}</h1>
          </div>
          <LanguageSelector variant="minimal" className="text-white" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6">
        {/* رسائل النجاح والخطأ */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{success}</span>
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* التبويبات */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'profile'
                  ? 'bg-gold/10 text-gold border-b-2 border-gold'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
              data-testid="tab-profile"
            >
              <div className="flex items-center justify-center gap-2">
                <User className="w-5 h-5" />
                {t('settings.profile') || 'الملف الشخصي'}
              </div>
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'password'
                  ? 'bg-gold/10 text-gold border-b-2 border-gold'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
              data-testid="tab-password"
            >
              <div className="flex items-center justify-center gap-2">
                <Lock className="w-5 h-5" />
                {t('settings.changePassword') || 'تغيير كلمة المرور'}
              </div>
            </button>
          </div>

          {/* محتوى الملف الشخصي */}
          {activeTab === 'profile' && (
            <form onSubmit={handleProfileUpdate} className="p-6 space-y-6">
              {/* صورة البروفايل */}
              <div className="flex flex-col items-center gap-4">
                <div className="relative">
                  <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-gold/20">
                    {profileData.profile_image ? (
                      <img
                        src={profileData.profile_image}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary/10">
                        <User className="w-16 h-16 text-primary/50" />
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-2 bg-gold text-white rounded-full shadow-lg hover:bg-gold-dark transition-colors"
                    data-testid="upload-image-btn"
                  >
                    <Camera className="w-5 h-5" />
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="image-input"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-sm text-gold hover:underline flex items-center gap-1"
                  >
                    <Upload className="w-4 h-4" />
                    {t('settings.uploadImage') || 'رفع صورة'}
                  </button>
                  {profileData.profile_image && (
                    <button
                      type="button"
                      onClick={handleDeleteImage}
                      className="text-sm text-red-500 hover:underline flex items-center gap-1"
                      data-testid="delete-image-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('settings.deleteImage') || 'حذف الصورة'}
                    </button>
                  )}
                </div>
              </div>

              {/* البريد الإلكتروني (للعرض فقط) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.email') || 'البريد الإلكتروني'}
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="w-full px-4 py-3 border rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-400">{t('settings.emailCannotChange') || 'لا يمكن تغيير البريد الإلكتروني'}</p>
              </div>

              {/* الاسم الكامل */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.fullName') || 'الاسم الكامل'}
                </label>
                <input
                  type="text"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                  data-testid="fullname-input"
                />
              </div>

              {/* رقم الجوال */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('form.phone') || 'رقم الجوال'}
                </label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                  placeholder="05xxxxxxxx"
                  data-testid="phone-input"
                />
              </div>

              {/* زر الحفظ */}
              <button
                type="submit"
                disabled={saving}
                className="w-full py-3 bg-gold text-white rounded-lg hover:bg-gold-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="save-profile-btn"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {t('msg.saving') || 'جاري الحفظ...'}
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    {t('btn.save') || 'حفظ التغييرات'}
                  </>
                )}
              </button>
            </form>
          )}

          {/* محتوى تغيير كلمة المرور */}
          {activeTab === 'password' && (
            <form onSubmit={handlePasswordChange} className="p-6 space-y-6">
              {/* كلمة المرور الحالية */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.currentPassword') || 'كلمة المرور الحالية'}
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.old ? 'text' : 'password'}
                    value={passwordData.old_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, old_password: e.target.value }))}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold outline-none ${isRTL ? 'pl-12' : 'pr-12'}`}
                    required
                    data-testid="old-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, old: !prev.old }))}
                    className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-4' : 'right-4'} text-gray-400 hover:text-gray-600`}
                  >
                    {showPasswords.old ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* كلمة المرور الجديدة */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.newPassword') || 'كلمة المرور الجديدة'}
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold outline-none ${isRTL ? 'pl-12' : 'pr-12'}`}
                    required
                    minLength={6}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-4' : 'right-4'} text-gray-400 hover:text-gray-600`}
                  >
                    {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400">{t('settings.passwordMinLength') || 'يجب أن تكون 6 أحرف على الأقل'}</p>
              </div>

              {/* تأكيد كلمة المرور الجديدة */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.confirmNewPassword') || 'تأكيد كلمة المرور الجديدة'}
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold outline-none ${isRTL ? 'pl-12' : 'pr-12'}`}
                    required
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    className={`absolute top-1/2 -translate-y-1/2 ${isRTL ? 'left-4' : 'right-4'} text-gray-400 hover:text-gray-600`}
                  >
                    {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* زر تغيير كلمة المرور */}
              <button
                type="submit"
                disabled={saving}
                className="w-full py-3 bg-gold text-white rounded-lg hover:bg-gold-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="change-password-btn"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {t('msg.saving') || 'جاري الحفظ...'}
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    {t('settings.changePassword') || 'تغيير كلمة المرور'}
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
